import { useMemo, useState, type FormEvent } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { Field } from '../ui/Field';
import { useAdmin } from '../AdminContext';
import { useCustomers } from '../customers/useCustomers';
import { contactHint, customerName, customerSearchText } from '../customers/customerRules';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { TimeChoice } from './TimeChoice';
import { RepeatChoice, type Repeat } from './RepeatChoice';
import { SeriesOutcome } from './SeriesOutcome';
import { DEFAULT_STAFF_ID, FALLBACK_DURATION } from '../../core/rules/agenda';
import { OWNER_STAFF_ID, canPerform, salonDayFor, staffList, staffName } from '../../core/rules/salonDays';
import { courseWarning } from '../../core/rules/staffPermissions';
import { useRequestId } from '../../hooks/useRequestId';
import { isValidEmail } from '../../core/rules/contact';
import { dayMedium } from '../../core/format/dates';
import type { AgendaAppointment, AgendaBlock, AgendaNewRequest, AgendaSeriesRequest, AgendaSeriesResult } from '../../core/types/agenda';
import type { CustomerSummary } from '../../core/types/adminRequests';
type Props = {
  initialDate: string;
  initialTime: string;
  appointments: AgendaAppointment[];
  blocks: AgendaBlock[];
  onSubmit: (req: AgendaNewRequest) => Promise<AgendaAppointment | null>;
  /** Herhalen (alleen de eigenaar). Ontbreekt = geen keuze "Herhalen". */
  onSubmitSeries?: (req: AgendaSeriesRequest) => Promise<AgendaSeriesResult | null>;
  onDone: (a: AgendaAppointment) => void;
  onCancel: () => void;
};
const MAX_MATCHES = 6;

/** Inplannen door Sarah: bestaande klant of gast, behandeling, dag en tijd. Direct bevestigd. */
export function NewAppointmentForm({
  initialDate,
  initialTime,
  appointments,
  blocks,
  onSubmit,
  onSubmitSeries,
  onDone,
  onCancel
}: Props) {
  const {
    catalog,
    index,
    site
  } = useAdmin();
  const {
    customers,
    status: customersStatus
  } = useCustomers();
  const [who, setWho] = useState<'klant' | 'gast'>('klant');
  const [query, setQuery] = useState('');
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const [guest, setGuest] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [treatment, setTreatment] = useState(catalog.treatments[0]?.slug ?? '');
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [overlap, setOverlap] = useState(false);
  const [repeat, setRepeat] = useState<Repeat>(null);
  // Na een reeks met overgeslagen datums: die blijven in beeld tot Sarah verder gaat.
  const [seriesResult, setSeriesResult] = useState<AgendaSeriesResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Eén id per inhoud: dubbelklikken of een herhaling plant niet dubbel in; een andere keuze is een nieuwe afspraak.
  const requestId = useRequestId(JSON.stringify([who, customer?.id, guest, treatment, date, time, overlap, repeat]));
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return customers.filter(c => customerSearchText(c).includes(q)).slice(0, MAX_MATCHES);
  }, [query, customers]);
  const duration = index.treatmentBySlug(treatment)?.durationMinutes ?? FALLBACK_DURATION;
  // Wie er die dag werkt doet de afspraak; op een dichte dag (Sarah plant bewust buiten de tijden) is dat Sarah.
  const performer = salonDayFor(site.openingHours, site.salonExceptions, date)?.staffId ?? DEFAULT_STAFF_ID;
  const team = staffList(site.staff);
  const performerWarning = date && treatment && !canPerform(team, performer, treatment) ? `${staffName(team, performer)} doet deze behandeling niet.` : '';
  // Kuursessie bij een ander dan bij wie de kuur loopt: alleen een waarschuwing.
  const runningCourse = who === 'klant' && customer?.course && customer.course.treatmentSlug === treatment && customer.course.done < customer.course.total ? customer.course : null;
  const kuurWarning = date && runningCourse ? courseWarning(runningCourse.staffId || OWNER_STAFF_ID, performer, team) : '';
  const guestOk = guest.name.trim() !== '' && isValidEmail(guest.email);
  const canSubmit = Boolean(treatment && date && time && (who === 'klant' ? customer : guestOk)) && !submitting;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const req: AgendaNewRequest = {
      requestId,
      treatmentSlug: treatment,
      date,
      time,
      staffId: performer,
      ...(who === 'klant' && customer ? {
        customerId: customer.id
      } : {
        guest: {
          name: guest.name.trim(),
          email: guest.email.trim(),
          phone: guest.phone.trim() || undefined
        }
      }),
      ...(overlap ? {
        allowOverlap: true
      } : {})
    };
    if (repeat && onSubmitSeries) {
      const result = await onSubmitSeries({
        ...req,
        ...repeat
      });
      setSubmitting(false);
      if (!result) return;
      // Alles gelukt: meteen naar de eerste afspraak. Anders eerst laten zien wat niet lukte.
      if (result.skipped.length === 0 && result.created[0]) onDone(result.created[0]);else setSeriesResult(result);
      return;
    }
    const a = await onSubmit(req);
    setSubmitting(false);
    if (a) onDone(a);
  };
  if (seriesResult) {
    return <SeriesOutcome result={seriesResult} onOpen={onDone} onClose={onCancel} data-id="element-434" data-mp="355tqytix2nwx" />;
  }
  return <form onSubmit={submit} className="space-y-5" noValidate data-id="element-435" data-mp="307if6d92a3c2">
      <div data-id="element-436" data-mp="309ctv9tqoz7p">
        <h2 className="font-display text-[1.4rem] font-light text-ink" data-id="element-437" data-mp="309mhiqrbaua8">Nieuwe afspraak</h2>
        <p className="mt-0.5 text-[13px] text-muted" data-id="element-438" data-mp="2va9v274crp86">Door jou ingepland is meteen bevestigd. De klant krijgt een mail met agenda-uitnodiging.</p>
      </div>

      <div role="radiogroup" aria-label="Voor wie" className="grid grid-cols-2 gap-1 rounded-full bg-sand p-1" data-id="element-439" data-mp="2mgwwaojlxte6">
        {(['klant', 'gast'] as const).map(w => <button key={w} type="button" role="radio" aria-checked={who === w} onClick={() => setWho(w)} className={`rounded-full py-2 text-[13px] transition-colors duration-150 ease-soft ${who === w ? 'bg-white text-ink shadow-sm' : 'text-muted hover:text-ink'}`} data-id="element-440" data-mp="2chedwcjcifo8">
            {w === 'klant' ? 'Bestaande klant' : 'Nieuwe gast'}
          </button>)}
      </div>

      {who === 'klant' ? customer ? <div className="flex items-center justify-between gap-3 rounded-md bg-sand px-3 py-2.5" data-id="element-441" data-mp="1svcjr3rzdr4p">
            <div className="min-w-0" data-id="element-442" data-mp="2gkyi5p0j42bn">
              <p className="truncate text-[14px] text-ink" data-id="element-443" data-mp="2gm06gv6k2lbb">{customerName(customer)}</p>
              <p className="truncate text-[12px] text-muted" data-id="element-444" data-mp="2lji800i8ty9a">
                {contactHint(customer)}
                {customer.course && ` · kuur ${customer.course.done}/${customer.course.total}`}
              </p>
            </div>
            <button type="button" onClick={() => setCustomer(null)} className={btnGhost} aria-label="Andere klant kiezen" data-id="element-445" data-mp="2bsnimn9paijm">
              <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-446" data-mp="34b1a854g18a9" />
            </button>
          </div> : <Field label="Klant" as="div" data-id="element-447" data-mp="svajk01n3hvb">
            <div className="relative" data-id="element-448" data-mp="stzdftkq1y11">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-449" data-mp="nxiowy9m1hnf" />
              <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Zoek op naam of e-mail" aria-label="Zoek een klant" className={`${inputCls} pl-9`} data-id="element-450" data-mp="12nyawf9uotmn" />
            </div>
            {customersStatus === 'error' && <p className="mt-2 text-[12px] text-clay" data-id="element-451" data-mp="jvnorx3f1ab8">Klanten laden lukte niet. Plan haar in als gast, of probeer het zo opnieuw.</p>}
            {matches.length > 0 && <ul className="mt-2 divide-y divide-line border-y border-line" data-id="element-452" data-mp="1vaj1hdii51p5">
                {matches.map(c => <li key={c.id} data-id="element-453" data-mp="1qbzfjd3wrpws">
                    <button type="button" onClick={() => {
            setCustomer(c);
            if (c.course) setTreatment(c.course.treatmentSlug);
          }} className="flex w-full items-baseline justify-between gap-3 px-3 py-2.5 text-left text-[14px] transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-454" data-mp="1qay251zf6rbw">
                      <span className="truncate text-ink" data-id="element-455" data-mp="7cs2xwmwnzef">{customerName(c)}</span>
                      <span className="shrink-0 text-[12px] text-muted" data-id="element-456" data-mp="7euoo4h0z6dc">{c.lastVisit ? `laatst ${dayMedium(c.lastVisit)}` : 'nog niet geweest'}</span>
                    </button>
                  </li>)}
              </ul>}
            {query.trim() && customersStatus === 'ready' && matches.length === 0 && <p className="mt-2 text-[12px] text-muted" data-id="element-457" data-mp="2i5cqjvewozjg">
                Niemand gevonden.{' '}
                <button type="button" onClick={() => setWho('gast')} className="text-ink underline underline-offset-4" data-id="element-458" data-mp="2n3e98f8f6wue">
                  Als nieuwe gast inplannen
                </button>
              </p>}
          </Field> : <div className="grid gap-3 sm:grid-cols-2" data-id="element-459" data-mp="2asdgavf6dsop">
          <Field label="Naam" className="sm:col-span-2" data-id="element-460" data-mp="25tsit3rb5n3y">
            <input value={guest.name} onChange={e => setGuest(g => ({
          ...g,
          name: e.target.value
        }))} autoComplete="off" className={inputCls} data-id="element-461" data-mp="25s91d9tx160l" />
          </Field>
          <Field label="E-mailadres" hint="Voor de bevestiging en de herinnering." data-id="element-462" data-mp="2yhy69y9qxq7h">
            <input type="email" value={guest.email} onChange={e => setGuest(g => ({
          ...g,
          email: e.target.value
        }))} autoComplete="off" className={inputCls} data-id="element-463" data-mp="2tl7o95edskjf" />
          </Field>
          <Field label="Telefoon" hint="Optioneel. Handig als ze te laat is." data-id="element-464" data-mp="2jmi6dcmhj036">
            <input type="tel" value={guest.phone} onChange={e => setGuest(g => ({
          ...g,
          phone: e.target.value
        }))} autoComplete="off" className={inputCls} data-id="element-465" data-mp="1d5wp87x1cbbe" />
          </Field>
        </div>}

      <Field label="Behandeling" data-id="element-466" data-mp="3nrh9fpol1gho">
        <select value={treatment} onChange={e => setTreatment(e.target.value)} className={inputCls} data-id="element-467" data-mp="3nsiw6reynrf8">
          {catalog.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-468" data-mp="3iqjr9d3komg6">
              {t.name} · {t.durationMinutes} min
            </option>)}
        </select>
      </Field>

      <TimeChoice date={date} onDate={setDate} time={time} onTime={setTime} durationMinutes={duration} appointments={appointments} blocks={blocks} overlap={overlap} onOverlap={setOverlap} data-id="element-469" data-mp="3spsihy71zmyu" />

      {onSubmitSeries && <RepeatChoice date={date} value={repeat} onChange={setRepeat} data-id="element-470" data-mp="k9plcmdkhgor" />}

      {date && team.length > 1 && <p className="text-[13px] text-muted" aria-live="polite" data-id="element-471" data-mp="fcz7m5p8ix6i">
          Uitvoerder: <span className="font-medium text-ink" data-id="element-472" data-mp="2m1c5q52yedc9">{staffName(team, performer)}</span>
          {performerWarning && <span className="mt-0.5 block text-clay" data-id="element-473" data-mp="2h3u1ut3smob4">{performerWarning}</span>}
          {kuurWarning && <span className="mt-0.5 block text-ink" data-id="element-474" data-mp="wkqb5xbim4q8">{kuurWarning}</span>}
        </p>}

      <div className="flex flex-wrap items-center gap-2 pt-1" data-id="element-475" data-mp="11kmfjxo4jy6b">
        <button type="submit" disabled={!canSubmit} aria-busy={submitting} className={btnPrimary} data-id="element-476" data-mp="11m60w2on0zey">
          {submitting ? 'Inplannen…' : repeat ? `${repeat.count} keer inplannen` : 'Inplannen'}
        </button>
        <button type="button" onClick={onCancel} className={btnGhost} data-id="element-477" data-mp="duid3tmoa4gw">
          Annuleer
        </button>
      </div>
    </form>;
}